import { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Avatar, Box, Button, Chip, Grid, Paper, Stack, Tab, Tabs, Typography, Skeleton, alpha, useTheme,
} from '@mui/material';
import { ArrowBack, Info, Payments, Timeline, ListAlt } from '@mui/icons-material';
import { GridColDef } from '@mui/x-data-grid';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import EnhancedDataGrid from '../../components/ui/EnhancedDataGrid';
import AmountBreakdown from '../../components/ui/AmountBreakdown';
import { getOrderGodView } from '../../services/opsViewService';
import { formatMoney } from '../../utils/currency';

const TEAL = '#0d9488';
const TABS = [
  { key: 'overview', label: 'Overview', icon: <Info fontSize="small" /> },
  { key: 'items', label: 'Items', icon: <ListAlt fontSize="small" /> },
  { key: 'fare', label: 'Fare', icon: <Payments fontSize="small" /> },
  { key: 'timeline', label: 'Timeline', icon: <Timeline fontSize="small" /> },
] as const;

export default function OrderDetailPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { orderId = '' } = useParams<{ orderId: string }>();
  const location = useLocation();
  const backTo = (location.state as { from?: string } | null)?.from || '/reports';
  const [tab, setTab] = useState<(typeof TABS)[number]['key']>('overview');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['order-god-view', orderId],
    queryFn: () => getOrderGodView(orderId),
    enabled: !!orderId,
  });

  const title = data?.orderNumber || 'Order';
  const fare = (data?.fare || {}) as Record<string, unknown>;
  const fareRows = Object.entries(fare)
    .filter(([, v]) => typeof v === 'number')
    .map(([k, v]) => ({
      _id: k,
      label: k.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase()),
      value: formatMoney(v),
    }));
  const timelineRows = (data?.timeline || []).map((e) => ({
    _id: e.key,
    label: e.label,
    at: e.at ? new Date(e.at).toLocaleString() : '—',
    note: e.note || '',
  }));

  const itemCols: GridColDef[] = [
    { field: 'name', headerName: 'Item', flex: 1, minWidth: 180 },
    { field: 'qty', headerName: 'Qty', width: 90 },
    { field: 'price', headerName: 'Price', width: 120, valueGetter: (_v, row) => `${formatMoney(row.price ?? 0)}` },
    { field: 'total', headerName: 'Total', width: 120, valueGetter: (_v, row) => `${formatMoney(row.total ?? 0)}` },
  ];

  return (
    <PageShell maxWidth={false}>
      <PageHeader
        eyebrow="Orders"
        title={title}
        subtitle="Order detail — items, customer, partner, fare, and timeline"
        breadcrumbs={[{ label: 'Reports', href: '/reports' }, { label: title }]}
        actions={
          <Button startIcon={<ArrowBack />} variant="outlined" onClick={() => navigate(backTo)}>
            Back
          </Button>
        }
      />

      {isError && (
        <Paper sx={{ p: 3, mb: 2, borderRadius: 2 }}>
          <Typography color="error">Could not load this order.</Typography>
        </Paper>
      )}

      <Grid container spacing={2} alignItems="stretch">
        <Grid item xs={12} md={3} lg={3}>
          <Paper elevation={0} sx={{ p: 2.5, height: '100%', borderRadius: 2.5, border: 1, borderColor: 'divider' }}>
            {isLoading ? (
              <Stack alignItems="center" spacing={1}>
                <Skeleton variant="circular" width={84} height={84} />
                <Skeleton width={140} />
              </Stack>
            ) : (
              <>
                <Stack alignItems="center" spacing={1}>
                  <Avatar sx={{ width: 84, height: 84, bgcolor: 'grey.200', fontSize: 32, color: 'text.secondary' }}>O</Avatar>
                  <Typography fontWeight={800}>{title}</Typography>
                  <Chip size="small" label={String(data?.kind || 'order')} />
                  <Chip size="small" label={String(data?.status || '').replace(/_/g, ' ')} />
                </Stack>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', my: 2 }} />
                <Stack spacing={1.25}>
                  {[
                    ['Order ID', data?.orderNumber || '—'],
                    ['Type', data?.kind || '—'],
                    ['Status', String(data?.status || '').replace(/_/g, ' ') || '—'],
                    ['Payment', data?.paymentMethod || '—'],
                    ['Total', `${formatMoney(data?.total ?? 0)}`],
                    ['Pickup', data?.pickup || '—'],
                    ['Drop', data?.drop || '—'],
                  ].map(([k, v]) => (
                    <Stack key={k} direction="row" justifyContent="space-between" spacing={1}>
                      <Typography variant="body2" color="text.secondary">{k}</Typography>
                      <Typography variant="body2" fontWeight={700} textAlign="right">{v}</Typography>
                    </Stack>
                  ))}
                </Stack>
                {data?.customer && (
                  <Button
                    fullWidth sx={{ mt: 2 }} variant="outlined"
                    onClick={() => navigate(`/customers/${data.customer!._id}`, { state: { from: `/orders/${orderId}` } })}
                  >
                    Customer: {data.customer.name}
                  </Button>
                )}
                {data?.partner && (
                  <Button
                    fullWidth sx={{ mt: 1 }} variant="outlined"
                    onClick={() => navigate(`/partners/${data.partner!._id}`, { state: { from: `/orders/${orderId}` } })}
                  >
                    Partner: {data.partner.name}
                  </Button>
                )}
              </>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={9} lg={9} sx={{ minWidth: 0 }}>
          <Paper elevation={0} sx={{ borderRadius: 2.5, border: 1, borderColor: 'divider', overflow: 'hidden' }}>
            <Box sx={{ px: 1, pt: 1, borderBottom: 1, borderColor: 'divider', bgcolor: alpha(theme.palette.primary.main, 0.03) }}>
              <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                variant="scrollable"
                allowScrollButtonsMobile
                TabIndicatorProps={{ sx: { display: 'none' } }}
              >
                {TABS.map((t) => (
                  <Tab
                    key={t.key}
                    value={t.key}
                    icon={t.icon}
                    iconPosition="start"
                    label={t.label}
                    sx={{
                      minHeight: 42, textTransform: 'none', fontWeight: 700, borderRadius: 1, mx: 0.25,
                      color: 'text.secondary',
                      '&.Mui-selected': { bgcolor: TEAL, color: '#fff' },
                      '& .MuiTab-iconWrapper': { color: 'inherit' },
                    }}
                  />
                ))}
              </Tabs>
            </Box>
            <Box sx={{ p: 2 }}>
              {isLoading ? <Skeleton height={280} /> : tab === 'items' ? (
                <EnhancedDataGrid
                  rows={data?.items || []}
                  columns={itemCols}
                  getRowId={(r) => String(r._id)}
                  autoHeight
                  disableLocalSearch
                  exportFileName={`${title}-items`}
                />
              ) : tab === 'overview' ? (
                <Stack spacing={1.25}>
                  <AmountBreakdown
                    fare={fare}
                    total={Number(data?.total ?? fare.total ?? 0)}
                    paymentMethod={data?.paymentMethod}
                    paymentStatus={data?.paymentStatus}
                  />
                  {(data?.fields || []).map(([k, v]) => (
                    <Stack key={k} direction="row" justifyContent="space-between" sx={{ py: 0.75, borderBottom: 1, borderColor: 'divider' }}>
                      <Typography variant="body2" color="text.secondary">{k}</Typography>
                      <Typography variant="body2" fontWeight={700}>{v || '—'}</Typography>
                    </Stack>
                  ))}
                </Stack>
              ) : tab === 'fare' ? (
                <EnhancedDataGrid
                  rows={fareRows}
                  columns={[
                    { field: 'label', headerName: 'Component', flex: 1, minWidth: 160 },
                    { field: 'value', headerName: 'Amount', width: 160 },
                  ]}
                  getRowId={(r) => String(r._id)}
                  autoHeight
                  disableLocalSearch
                  exportFileName={`${title}-fare`}
                />
              ) : (
                <EnhancedDataGrid
                  rows={timelineRows}
                  columns={[
                    { field: 'label', headerName: 'Event', flex: 1, minWidth: 140 },
                    { field: 'at', headerName: 'Time', width: 200 },
                    { field: 'note', headerName: 'Note', flex: 1, minWidth: 140 },
                  ]}
                  getRowId={(r) => String(r._id)}
                  autoHeight
                  disableLocalSearch
                  exportFileName={`${title}-timeline`}
                />
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </PageShell>
  );
}
